import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { questionsWithVisibility, QuestionWithVisibility } from "../../surveys/survey";
import {
  SurveyAnswerFn,
  SurveyAnswers,
  useParsedSurveyAnswers,
  useSurvey,
} from "../../../common/hooks/api/useSurvey";
import clsx from "clsx";
import { BUSINESS_SURVEY_NAME, businessSurvey } from "../../surveys/businessSurvey";
import { PageLoading } from "../../components";
import {
  SurveyQuestion,
  SurveyQuestionProps,
} from "../../components/get-compliant/survey/SurveyQuestion";
import { HR_SURVEY_NAME, hrSurvey } from "../../surveys/hrSurvey";
import { ChevronDownIcon, ChevronUpIcon, FlagIcon } from "@heroicons/react/24/outline";
import { FlagIcon as FlagIconFilled, CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  EEA_UK_SURVEY_NAME,
  eeaUkSurvey,
  HAS_DATA_PRIVACY_OFFICER_SLUG,
  HAS_DESIGNATED_REP_SLUG,
  hasDataPrivacyOfficer,
  hasDesignatedRepresentative,
  isDataPrivacyOfficerRequired,
} from "../../surveys/eeaUkSurvey";
import {
  computeBusinessProfileSurveyCompletion,
  isBusinessProfileQuestionAnswered,
} from "../../surveys/businessProfileSurveyCompletion";
import { filterVendorsForDataSourceSurveyQuestion } from "../../components/get-compliant/survey/custom/AddDataSourceQuestion";
import { Breadcrumbs, Button, SimpleCheckboxControlled } from "@truevault/aquilo";
import {
  PrivacyCenterSettingsProps,
  usePrivacyCenterSettings,
} from "../../components/superorg/settings/PrivacyCenterSettings";
import { DpoValues, EditDpoDialog } from "../../components/dialog/EditDpoDialog";
import { PrivacyCenterPipedaPrivacyOfficerRow } from "../../components/organisms/settings/rows/PrivacyCenterPipedaPrivacyOfficerRow";
import { EditEeaContactDialog, EeaContactValues } from "../../components/dialog/EEAContactDialog";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { FinancialIncentivesQuestion } from "../get-compliant/survey/custom/FinancialIncentivesQuestion";
import { CONSUMER_INCENTIVES_SURVEY_NAME } from "../../surveys/consumerCollectionIncentivesSurvey";
import { CONSUMER_FINANCIAL_INCENTIVES_QUESTION } from "../../surveys/orgSettings";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../pages/get-compliant/data-recipients/shared";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { SurveyBooleanQuestionProps } from "../get-compliant/survey/SurveyBooleanQuestion";
import { selectAnswerDisplayText } from "../get-compliant/survey/SurveySelectQuestion";

export function SurveyAnswersReview({
  render,
}: {
  // isSurveyComplete is null while survey data is still loading
  render: (
    isSurveyComplete: boolean | null,
    pageBody: React.ReactNode,
    meta: { showOnboardingFooter: boolean },
  ) => React.ReactElement;
}) {
  const [org] = usePrimaryOrganization();

  const { settings: pcSettings, privacyCenter } = usePrivacyCenterSettings();

  const {
    answers: bizAnswers,
    setAnswer: setBizAnswer,
    flaggedForReviewSlugs: bizFlaggedForReviewSlugs,
  } = useSurvey(org?.id, BUSINESS_SURVEY_NAME);

  const {
    answers: eeaAnswers,
    setAnswer: setEeaAnswer,
    flaggedForReviewSlugs: eeaFlaggedForReviewSlugs,
  } = useSurvey(org?.id, EEA_UK_SURVEY_NAME);

  const {
    answers: hrAnswers,
    setAnswer: setHrAnswer,
    flaggedForReviewSlugs: hrFlaggedForReviewSlugs,
  } = useSurvey(org?.id, HR_SURVEY_NAME);

  const {
    answers: incentivesAnswers,
    setAnswer: setIncentivesAnswer,
    flaggedForReviewSlugs: incentivesFlaggedForReviewSlugs,
  } = useSurvey(org?.id, CONSUMER_INCENTIVES_SURVEY_NAME);
  const parsedIncentivesAnswers = useParsedSurveyAnswers(incentivesAnswers);

  // Dialogs for editing DPO and Designated Rep details
  const [isEditDpoDialogOpen, setIsEditDpoDialogOpen] = useState(false);
  const [isDesignatedRepDialogOpen, setIsDesignatedRepDialogOpen] = useState(false);

  // Note that `orgVendors` and `sellingAndSharingAnswers` are not shown in the survey, but used to compute sourcesSurveyDefinition
  const [orgVendors] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ ["CONSUMER"],
    /* onlyUseSettled = */ true,
  );
  const { answers: sellingAndSharingAnswers } = useSurvey(org?.id, SELLING_AND_SHARING_SURVEY_NAME);
  // Used to list the org's selling vendors and their sensitive PICs in the New Jersey question help text
  const { data: dataMap } = useDataMap(org?.id);

  if (
    !org ||
    !bizAnswers ||
    !hrAnswers ||
    !eeaAnswers ||
    !sellingAndSharingAnswers ||
    !orgVendors
  ) {
    return render(null, <PageLoading />, { showOnboardingFooter: false });
  }

  const bizSurveyDefinition = businessSurvey(
    bizAnswers,
    sellingAndSharingAnswers,
    orgVendors,
    dataMap,
  );
  const bizVisibleQuestions = questionsWithVisibility(bizSurveyDefinition, bizAnswers).filter(
    (q) => q.visible,
  );

  const eeaSurveyDefinition = eeaUkSurvey(eeaAnswers, privacyCenter, pcSettings);
  const eeaVisibleQuestions = questionsWithVisibility(eeaSurveyDefinition, eeaAnswers).filter(
    (q) => q.visible,
  );

  const hrSurveyDefinition = hrSurvey(hrAnswers, org?.featureGdpr);
  const hrVisibleQuestions = questionsWithVisibility(hrSurveyDefinition, hrAnswers).filter(
    (q) => q.visible,
  );

  const financialIncentiveAnswer =
    parsedIncentivesAnswers[CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug];

  const isDpoRequired = org?.featureGdpr && isDataPrivacyOfficerRequired(eeaAnswers, privacyCenter);
  const hasDpo = Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail);
  const hasDesignatedRep = hasDesignatedRepresentative(pcSettings);
  const hasDesignatedRepTask = Boolean(privacyCenter?.hasDesignatedRepTask);

  const {
    isBizSurveyComplete,
    isHrSurveyComplete,
    isEeaUkSurveyComplete,
    isFinancialIncentivesSectionComplete,
    isSurveyComplete,
  } = computeBusinessProfileSurveyCompletion({
    featureGdpr: org?.featureGdpr,
    privacyCenter,
    pcSettings,
    bizVisibleQuestions,
    bizAnswers,
    hrVisibleQuestions,
    hrAnswers,
    eeaVisibleQuestions,
    eeaAnswers,
    financialIncentiveAnswer,
  });

  const showOnboardingFooter = !org.finishedOnboardingLatch;

  let pageBody = (
    <>
      <div className="tw-boundary">
        <Breadcrumbs
          title="Business Profile"
          source="Data Map"
          sourceRoute={"#"}
          hideHomeIcon={true}
        />

        <div className="mt-[24px] mb-[50px] space-y-[24px]">
          <ReviewAnswersPane
            title="Data Collection Practices"
            questionsWithVisibility={bizVisibleQuestions}
            isSectionComplete={isBizSurveyComplete}
            answers={bizAnswers}
            setAnswer={setBizAnswer}
            initialIsCollapsed={false}
            flaggedForReviewSlugs={bizFlaggedForReviewSlugs}
            pcSettings={pcSettings}
            openDpoDialog={() => setIsEditDpoDialogOpen(true)}
            openDesignatedRepDialog={() => setIsDesignatedRepDialogOpen(true)}
            {...{ isDpoRequired, hasDpo, hasDesignatedRep, hasDesignatedRepTask }}
          />

          {org?.featureGdpr && (
            <ReviewAnswersPane
              title="European Data Practices"
              questionsWithVisibility={eeaVisibleQuestions}
              isSectionComplete={isEeaUkSurveyComplete}
              answers={eeaAnswers}
              setAnswer={setEeaAnswer}
              flaggedForReviewSlugs={eeaFlaggedForReviewSlugs}
              pcSettings={pcSettings}
              openDpoDialog={() => setIsEditDpoDialogOpen(true)}
              openDesignatedRepDialog={() => setIsDesignatedRepDialogOpen(true)}
              {...{ isDpoRequired, hasDpo, hasDesignatedRep, hasDesignatedRepTask }}
            />
          )}

          {org?.featurePipeda && (
            <CollapsiblePane title="Canadian Data Practices">
              <div style={{ padding: "16px 24px" }}>
                <PrivacyCenterPipedaPrivacyOfficerRow
                  organization={pcSettings.organization}
                  vertical
                  useNewDesign
                  compact
                  dpoName={pcSettings.dpoName}
                  dpoEmail={pcSettings.dpoEmail}
                  dpoPhone={pcSettings.dpoPhone}
                  dpoAddress={pcSettings.dpoAddress}
                  pipedaOfficerName={pcSettings.pipedaOfficerName}
                  pipedaOfficerEmail={pcSettings.pipedaOfficerEmail}
                  pipedaOfficerPhone={pcSettings.pipedaOfficerPhone}
                  pipedaOfficerAddress={pcSettings.pipedaOfficerAddress}
                  handleFormSubmit={pcSettings.handleFormSubmit}
                />
              </div>
            </CollapsiblePane>
          )}

          <ReviewAnswersPane
            title="HR Practices"
            questionsWithVisibility={hrVisibleQuestions}
            isSectionComplete={isHrSurveyComplete}
            answers={hrAnswers}
            setAnswer={setHrAnswer}
            flaggedForReviewSlugs={hrFlaggedForReviewSlugs}
            pcSettings={pcSettings}
            openDpoDialog={() => setIsEditDpoDialogOpen(true)}
            openDesignatedRepDialog={() => setIsDesignatedRepDialogOpen(true)}
            {...{ isDpoRequired, hasDpo, hasDesignatedRep, hasDesignatedRepTask }}
          />

          <FinancialIncentivesPane
            org={org}
            isSectionComplete={isFinancialIncentivesSectionComplete}
            incentivesAnswers={incentivesAnswers}
            setIncentivesAnswer={setIncentivesAnswer}
            flaggedForReviewSlugs={incentivesFlaggedForReviewSlugs}
          />
        </div>
      </div>

      <EditDpoDialog
        open={isEditDpoDialogOpen}
        onClose={() => setIsEditDpoDialogOpen(false)}
        onDone={async (values: DpoValues) => {
          await pcSettings.handleFormSubmit({
            dpoOfficerName: values.name,
            dpoOfficerEmail: values.email,
            dpoOfficerPhone: values.phone,
            dpoOfficerAddress: values.address,
          });
        }}
        values={{
          name: pcSettings?.dpoName,
          email: pcSettings?.dpoEmail,
          phone: pcSettings?.dpoPhone,
          address: pcSettings?.dpoAddress,
        }}
      />

      <EditEeaContactDialog
        open={isDesignatedRepDialogOpen}
        onClose={() => setIsDesignatedRepDialogOpen(false)}
        onDone={async (values: EeaContactValues) => {
          await pcSettings.handleFormSubmit({
            eeaContactName: values.eeaContactName,
            eeaContactAddress: values.eeaContactAddress,
            eeaContactEmail: values.eeaContactEmail,
            eeaContactPhone: values.eeaContactPhone,
          });

          await setEeaAnswer(HAS_DESIGNATED_REP_SLUG, "true");
        }}
        values={{
          eeaContactName: pcSettings.eeaContactName,
          eeaContactAddress: pcSettings?.eeaContactAddress,
          eeaContactEmail: pcSettings?.eeaContactEmail,
          eeaContactPhone: pcSettings?.eeaContactPhone,
        }}
      />
    </>
  );

  return <>{render(isSurveyComplete, pageBody, { showOnboardingFooter })}</>;
}

function FinancialIncentivesPane({
  org,
  isSectionComplete,
  incentivesAnswers,
  setIncentivesAnswer,
  flaggedForReviewSlugs,
}: {
  org: OrganizationDto;
  isSectionComplete: boolean;
  incentivesAnswers: SurveyAnswers;
  setIncentivesAnswer: SurveyAnswerFn;
  flaggedForReviewSlugs: string[];
}) {
  const [isEditing, setIsEditing] = useState(false);

  const parsedIncentivesAnswers = useParsedSurveyAnswers(incentivesAnswers);

  if (!incentivesAnswers) {
    return null;
  }

  let financialIncentiveAnswer =
    parsedIncentivesAnswers[CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug];

  let isFlaggedForReview = (flaggedForReviewSlugs || []).includes(
    CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug,
  );

  return (
    <CollapsiblePane
      title="Financial Incentives"
      showIncompleteBadge={!isSectionComplete}
      headerAction={
        <UncheckAllFlagsButton
          flaggedCount={isFlaggedForReview ? 1 : 0}
          onClick={async () => {
            await setIncentivesAnswer(
              CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug,
              isSectionComplete ? financialIncentiveAnswer.toString() : "",
              CONSUMER_FINANCIAL_INCENTIVES_QUESTION.question,
              /* flaggedForReview = */ false,
            );
          }}
        />
      }
    >
      <div className="px-[24px] py-[16px] flex items-start justify-between" style={{ gap: "24px" }}>
        <div>
          <div className="text-[16px] leading-[24px] font-medium text-neutral-900 mb-[6px]">
            {CONSUMER_FINANCIAL_INCENTIVES_QUESTION.question}
          </div>
          {isEditing && (
            <div className="my-[6px] text-[16px] leading-[24px] text-neutral-600">
              {CONSUMER_FINANCIAL_INCENTIVES_QUESTION.help}
            </div>
          )}
          {isEditing ? (
            <div className="survey-review-inline">
              <FinancialIncentivesQuestion
                org={org}
                question={CONSUMER_FINANCIAL_INCENTIVES_QUESTION}
                answer={financialIncentiveAnswer}
                setAnswer={async (_question, answer: string) => {
                  await setIncentivesAnswer(
                    CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug,
                    answer,
                    CONSUMER_FINANCIAL_INCENTIVES_QUESTION.question,
                  );
                }}
                enableTask={true}
                useNewDesign={true}
                onlyShowInlineAnswerButtons={true}
                hideStayCompliantLinks={true}
              />
            </div>
          ) : isSectionComplete ? (
            <div className="text-[16px] leading-[24px] font-semibold text-primary-600">
              {financialIncentiveAnswer ? "Yes" : "No"}
            </div>
          ) : (
            <IncompleteBadge />
          )}
        </div>
        <div className="flex items-center">
          <div
            className="text-[16px] font-medium text-neutral-600 cursor-pointer underline"
            onClick={() => setIsEditing((s) => !s)}
          >
            {isEditing ? "Cancel" : "Edit"}
          </div>
          <div
            className="ml-[12px] cursor-pointer"
            onClick={() => {
              setIncentivesAnswer(
                CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug,
                isSectionComplete ? financialIncentiveAnswer.toString() : "",
                CONSUMER_FINANCIAL_INCENTIVES_QUESTION.question,
                !isFlaggedForReview,
              );
            }}
          >
            {isFlaggedForReview ? (
              <FlagIconFilled className="w-[24px] h-[24px] text-primary-500" />
            ) : (
              <FlagIcon className="w-[24px] h-[24px] text-primary-500" />
            )}
          </div>
        </div>
      </div>
    </CollapsiblePane>
  );
}

function ReviewAnswersPane({
  title,
  isSectionComplete,
  questionsWithVisibility,
  answers,
  setAnswer,
  flaggedForReviewSlugs,
  pcSettings,
  initialIsCollapsed = true,
  openDpoDialog,
  openDesignatedRepDialog,
  isDpoRequired,
  hasDpo,
  hasDesignatedRep,
  hasDesignatedRepTask,
}: {
  title: string;
  isSectionComplete: boolean;
  questionsWithVisibility: QuestionWithVisibility[];
  answers: SurveyAnswers;
  setAnswer: SurveyAnswerFn;
  flaggedForReviewSlugs: string[];
  pcSettings: PrivacyCenterSettingsProps;
  initialIsCollapsed?: boolean;
  openDpoDialog: () => void;
  openDesignatedRepDialog: () => void;
  isDpoRequired: boolean;
  hasDpo: boolean;
  hasDesignatedRep: boolean;
  hasDesignatedRepTask: boolean;
}) {
  const visibleQuestions = questionsWithVisibility.filter((q) => q.visible);
  const flaggedQuestions = visibleQuestions.filter((q) => flaggedForReviewSlugs.includes(q.slug));
  const uncheckAllFlags = async () => {
    for (const q of flaggedQuestions) {
      const questionText = q.plaintextQuestion || (typeof q.question == "string" ? q.question : "");
      await setAnswer(q.slug, answers[q.slug], questionText, /* flaggedForReview = */ false);
    }
  };
  return (
    <CollapsiblePane
      initialIsCollapsed={initialIsCollapsed}
      title={title}
      showIncompleteBadge={!isSectionComplete}
      headerAction={
        <UncheckAllFlagsButton flaggedCount={flaggedQuestions.length} onClick={uncheckAllFlags} />
      }
    >
      <div>
        {visibleQuestions.map((q, idx, a) => {
          return (
            <div
              key={q.slug}
              className={clsx(idx < a.length - 1 && "border-b border-solid border-gray-200")}
            >
              <InlineSurveyQuestionReview
                question={q}
                answer={answers[q.slug]}
                isFlaggedForReview={flaggedForReviewSlugs.includes(q.slug)}
                setAnswer={setAnswer}
                pcSettings={pcSettings}
                openDpoDialog={openDpoDialog}
                openDesignatedRepDialog={openDesignatedRepDialog}
                isDpoRequired={isDpoRequired}
                hasDpo={hasDpo}
                hasDesignatedRep={hasDesignatedRep}
                hasDesignatedRepTask={hasDesignatedRepTask}
              />
            </div>
          );
        })}
      </div>
    </CollapsiblePane>
  );
}

function CollapsiblePane({
  title,
  initialIsCollapsed = true,
  showIncompleteBadge = false,
  headerAction,
  children,
}: {
  title: string;
  initialIsCollapsed?: boolean;
  showIncompleteBadge?: boolean;
  /** Optional control on the right of the header (e.g. "Uncheck all flags"). */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [isCollapsed, setIsCollapsed] = useState(initialIsCollapsed);

  // Same chrome as the Vendor List category tables so the two pages read as one product: rounded
  // bordered card, white header row with a count pill and chevron, 1px row dividers.
  return (
    <div
      className="w-full max-w-[1143px] border border-solid border-neutral-200 rounded-xl shadow-[0px_1px_2px_0px_#1018280D]"
      style={{ overflow: "clip" }}
    >
      <div
        className={clsx(
          "bg-neutral-0 px-[24px] flex items-center justify-between cursor-pointer",
          !isCollapsed && "border-b border-solid border-neutral-200",
        )}
        style={{ paddingTop: 11, paddingBottom: 11 }}
        onClick={() => setIsCollapsed((c) => !c)}
      >
        <span className="font-medium text-[16px] text-neutral-900">{title}</span>
        <div className="flex items-center gap-[12px]">
          {headerAction}
          {showIncompleteBadge && <IncompleteBadge />}
          <button
            type="button"
            className="flex items-center text-neutral-700 cursor-pointer bg-transparent border-none p-0"
            aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
            aria-expanded={!isCollapsed}
            onClick={(e) => {
              e.stopPropagation();
              setIsCollapsed((c) => !c);
            }}
          >
            {isCollapsed ? (
              <ChevronDownIcon className="w-5 h-5" />
            ) : (
              <ChevronUpIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: isCollapsed ? "0fr" : "1fr",
          transition: "grid-template-rows 0.3s ease",
        }}
        aria-hidden={isCollapsed}
      >
        <div style={{ overflow: "hidden", minHeight: 0 }}>{children}</div>
      </div>
    </div>
  );
}

function InlineSurveyQuestionReview({
  question,
  answer,
  isFlaggedForReview,
  setAnswer,
  pcSettings,
  openDpoDialog,
  openDesignatedRepDialog,
  isDpoRequired,
  hasDpo,
  hasDesignatedRep,
  hasDesignatedRepTask,
}: {
  question: SurveyQuestionProps;
  answer: string;
  isFlaggedForReview: boolean;
  setAnswer: SurveyAnswerFn;
  pcSettings: PrivacyCenterSettingsProps;
  openDpoDialog: () => void;
  openDesignatedRepDialog: () => void;
  isDpoRequired: boolean;
  hasDpo: boolean;
  hasDesignatedRep: boolean;
  hasDesignatedRepTask: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const [eeaContactDoesNotApply, setEeaContactDoesNotApply] = useState(
    pcSettings.eeaContactDoesNotApply,
  );
  const [dpoDoesNotApply, setDpoDoesNotApply] = useState(pcSettings.dpoDoesNotApply);
  const [isSaving, setIsSaving] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [wrapperHeight, setWrapperHeight] = useState<string>("auto");

  useLayoutEffect(() => {
    if (!innerRef.current || wrapperHeight === "auto") return;
    const newHeight = innerRef.current.offsetHeight;
    const raf = requestAnimationFrame(() => setWrapperHeight(`${newHeight}px`));
    return () => cancelAnimationFrame(raf);
  }, [isEditing]);

  const toggleEditing = () => {
    if (wrapperRef.current) {
      setWrapperHeight(`${wrapperRef.current.offsetHeight}px`);
    }
    setIsEditing((s) => !s);
  };

  useEffect(() => {
    setEeaContactDoesNotApply(pcSettings.eeaContactDoesNotApply);
  }, [pcSettings.eeaContactDoesNotApply]);

  useEffect(() => {
    setDpoDoesNotApply(pcSettings.dpoDoesNotApply);
  }, [pcSettings.dpoDoesNotApply]);

  let questionLabel =
    question.settingsQuestionLabel ||
    question.plaintextQuestion ||
    question.question ||
    question.slug;
  // Since data Source questions already show the question text, don't show the question text  again if we're editing a data source question
  // The row owns the question and help text; the survey component's copies are hidden by the
  // .survey-review-inline styles (src/sass/widgets/business-profile-review.scss).
  let showQuestionLabel = true;

  let isDpoQuestion = question.slug === HAS_DATA_PRIVACY_OFFICER_SLUG;
  let isDesignatedRepQuestion = question.slug === HAS_DESIGNATED_REP_SLUG;
  // The wizard's "No" to the DPO (when one is required) and designated-rep questions means "I'll do
  // this later" (the backend opens a task pointing back here), not "does not apply", so only the
  // saved "does not apply" flags tick the checkboxes and disable the details buttons.
  const saysLater = answer === "false";

  // Same rule the section header uses (businessProfileSurveyCompletion), fed the row's live
  // "does not apply" state so a checkbox tick clears the badge immediately.
  const isAnswered = isBusinessProfileQuestionAnswered(
    question,
    { [question.slug]: answer },
    {
      isDpoRequired,
      hasDpo,
      dpoDoesNotApply: Boolean(dpoDoesNotApply),
      hasDesignatedRep,
      eeaContactDoesNotApply: Boolean(eeaContactDoesNotApply),
      hasDesignatedRepTask,
    },
  );

  return (
    <div className="px-[24px] py-[16px] flex items-start justify-between" style={{ gap: "24px" }}>
      <div
        ref={wrapperRef}
        style={{
          flex: 1,
          height: wrapperHeight,
          overflow: "hidden",
          transition:
            wrapperHeight === "auto" ? "none" : "height 400ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
        onTransitionEnd={() => setWrapperHeight("auto")}
      >
        <div ref={innerRef}>
          {showQuestionLabel && (
            <>
              <div className="pr-[12px] text-[16px] leading-[24px] font-medium text-neutral-900">
                {isEditing ? question.question : questionLabel}
              </div>
              {isEditing && question.helpText && (
                <div className="mt-[6px] text-[16px] leading-[24px] text-neutral-600">
                  {question.helpText}
                </div>
              )}
            </>
          )}
          <div className="mt-[6px]">
            {!isDpoQuestion &&
              !isDesignatedRepQuestion &&
              (isEditing ? (
                <div className="survey-review-inline mt-[8px]">
                  <SurveyQuestion
                    question={question}
                    answer={answer}
                    onAnswer={async (ans) => {
                      let questionText = question.plaintextQuestion;
                      if (!questionText && typeof question.question == "string") {
                        questionText = question.question;
                      }

                      setAnswer(
                        question.slug,
                        ans,
                        questionText,
                        /* flaggedForReview = */ isFlaggedForReview,
                      );

                      // For the DPO and Designated Rep questions, don't exit editing mode after an answer, since we also show separate buttons for editing those details
                      if (
                        question.slug !== HAS_DATA_PRIVACY_OFFICER_SLUG &&
                        question.slug !== HAS_DESIGNATED_REP_SLUG
                      ) {
                        if (wrapperRef.current) {
                          setWrapperHeight(`${wrapperRef.current.offsetHeight}px`);
                        }
                        setIsEditing(false);
                      }
                    }}
                    useNewDesign={true}
                    onlyShowInlineAnswerButtons={true}
                    showDoneAddingVendorsButton={true}
                  />
                </div>
              ) : isAnswered ? (
                <div className="text-[16px] leading-[24px] font-semibold text-primary-600">
                  {answerDisplayText(question, answer)}
                </div>
              ) : (
                <IncompleteBadge />
              ))}

            {isDpoQuestion && (
              <div>
                <div className="flex items-center mt-[6px]">
                  {isEditing && (
                    <div className="mr-[6px]">
                      <Button
                        color="secondary"
                        size="s"
                        disabled={Boolean(dpoDoesNotApply) && !hasDataPrivacyOfficer(pcSettings)}
                        onClick={openDpoDialog}
                      >
                        {hasDataPrivacyOfficer(pcSettings) ? "Edit" : "Add"} DPO Details
                      </Button>
                    </div>
                  )}
                  {!isEditing &&
                    !hasDataPrivacyOfficer(pcSettings) &&
                    (dpoDoesNotApply ? (
                      <div className="text-[16px] leading-[24px] font-semibold text-primary-600">
                        No (Does Not Apply)
                      </div>
                    ) : saysLater ? (
                      <div className="text-[16px] leading-[24px] font-semibold text-primary-600">
                        {isDpoRequired ? "Task created" : "No"}
                      </div>
                    ) : (
                      !isAnswered && <IncompleteBadge />
                    ))}
                  {hasDataPrivacyOfficer(pcSettings) && (
                    <div className="bg-green-100 inline-flex rounded-[6px] px-[12px] py-[7px] items-center self-stretch">
                      <div>
                        <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
                      </div>
                      <div className="ml-[12px] text-green-800 font-medium text-[12px] leading-4">
                        {pcSettings.dpoName}, {pcSettings.dpoEmail}
                      </div>
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className={clsx("mt-[12px]", isSaving && "opacity-50 cursor-not-allowed")}>
                    <SimpleCheckboxControlled
                      name="dpo-does-not-apply"
                      label="I do not require a Data Privacy Officer"
                      checked={Boolean(dpoDoesNotApply)}
                      onChange={async (newChecked: boolean) => {
                        setIsSaving(true);
                        try {
                          await pcSettings.handleFormSubmit({
                            dpoDoesNotApply: newChecked,
                          });
                          setDpoDoesNotApply(newChecked);
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      disabled={isSaving}
                    />
                  </div>
                )}
              </div>
            )}

            {isDesignatedRepQuestion && (
              <div>
                <div className="flex items-center mt-[6px]">
                  {isEditing && (
                    <div className="mr-[6px]">
                      <Button
                        color="secondary"
                        size="s"
                        disabled={
                          Boolean(eeaContactDoesNotApply) &&
                          !hasDesignatedRepresentative(pcSettings)
                        }
                        onClick={openDesignatedRepDialog}
                      >
                        {hasDesignatedRepresentative(pcSettings) ? "Edit" : "Add"} Designated Rep
                        Details
                      </Button>
                    </div>
                  )}
                  {!isEditing &&
                    !hasDesignatedRepresentative(pcSettings) &&
                    (eeaContactDoesNotApply ? (
                      <div className="text-[16px] leading-[24px] font-semibold text-primary-600">
                        No (Does Not Apply)
                      </div>
                    ) : hasDesignatedRepTask || saysLater ? (
                      <div className="text-[16px] leading-[24px] font-semibold text-primary-600">
                        Task created
                      </div>
                    ) : (
                      <IncompleteBadge />
                    ))}
                  {hasDesignatedRepresentative(pcSettings) && (
                    <div className="bg-green-100 inline-flex rounded-[6px] px-[12px] py-[7px] items-center self-stretch">
                      <div>
                        <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
                      </div>
                      <div className="ml-[12px] text-green-800 font-medium text-[12px] leading-4">
                        {pcSettings.eeaContactName}, {pcSettings.eeaContactEmail},{" "}
                        {pcSettings.eeaContactAddress}
                      </div>
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className={clsx("mt-[12px]", isSaving && "opacity-50 cursor-not-allowed")}>
                    <SimpleCheckboxControlled
                      name="eea-contact-does-not-apply"
                      label="I have read the requirements and article about Designated Representatives and this requirement does not apply to my organization."
                      checked={Boolean(eeaContactDoesNotApply)}
                      onChange={async (newChecked: boolean) => {
                        setIsSaving(true);
                        try {
                          await pcSettings.handleFormSubmit({
                            eeaContactDoesNotApply: newChecked,
                          });
                          setEeaContactDoesNotApply(newChecked);
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      disabled={isSaving}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center">
        <div
          className="text-[16px] font-medium text-neutral-600 cursor-pointer underline"
          onClick={toggleEditing}
        >
          {isEditing ? "Cancel" : "Edit"}
        </div>
        <div
          className="ml-[12px] cursor-pointer"
          onClick={() => {
            let questionText = question.plaintextQuestion;
            if (!questionText && typeof question.question == "string") {
              questionText = question.question;
            }
            setAnswer(question.slug, answer, questionText, !isFlaggedForReview);
          }}
        >
          {isFlaggedForReview ? (
            <FlagIconFilled className="w-[24px] h-[24px] text-primary-500" />
          ) : (
            <FlagIcon className="w-[24px] h-[24px] text-primary-500" />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Clears every "flagged for review" mark in a section in one click. Only shown while there is
 * something to clear — flags are rare, so there is no "flag all" counterpart.
 */
function UncheckAllFlagsButton({
  flaggedCount,
  onClick,
}: {
  flaggedCount: number;
  onClick: () => Promise<void>;
}) {
  const [isClearing, setIsClearing] = useState(false);
  if (flaggedCount === 0) {
    return null;
  }
  return (
    <button
      type="button"
      className="text-[14px] font-medium text-primary-600 underline cursor-pointer bg-transparent border-0 p-0 whitespace-nowrap disabled:cursor-not-allowed disabled:text-neutral-400"
      disabled={isClearing}
      onClick={async (e) => {
        e.stopPropagation();
        setIsClearing(true);
        try {
          await onClick();
        } finally {
          setIsClearing(false);
        }
      }}
    >
      {isClearing ? "Unchecking…" : `Uncheck all flags (${flaggedCount})`}
    </button>
  );
}

function IncompleteBadge() {
  return (
    <div
      className={clsx(
        "bg-amber-100 rounded-[100px] px-[10px] py-[2px] inline-flex items-center justify-center",
        "text-amber-800 font-medium text-[12px] whitespace-nowrap",
      )}
    >
      Incomplete
    </div>
  );
}

function answerDisplayText(question: SurveyQuestionProps, answer: string) {
  if (question.type === "boolean") {
    // Prefer the question's custom labels (e.g. "Confirmed" / "Remind Me" for the New Jersey selling question)
    const { yesLabel, noLabel, yesAnswerLabel, noAnswerLabel } =
      question as SurveyBooleanQuestionProps;
    if (answer === "true") {
      return yesAnswerLabel ?? (typeof yesLabel === "string" ? yesLabel : "Yes");
    }
    return noAnswerLabel ?? (typeof noLabel === "string" ? noLabel : "No");
  }

  if (question.type === "custom-add-data-source") {
    let filteredVendors = filterVendorsForDataSourceSurveyQuestion(question);
    let filteredVendorIds = new Set(filteredVendors.map((v) => v.id));
    return question.sources
      .filter((s) => filteredVendorIds.has(s.vendorId))
      .map((s) => s.name)
      .join(", ");
  }

  if (question.type === "checkboxes") {
    let parsedAnswer = JSON.parse(answer || "[]");
    return question.options
      .filter((o) => parsedAnswer.includes(o.key))
      .map((o) => o.label)
      .join(", ");
  }

  if (question.type === "select") {
    return selectAnswerDisplayText(question, answer);
  }

  return answer;
}
